import React, { useState, useEffect, useMemo } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Button,
  ButtonGroup,
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), maxWidth: 900, margin: "0 auto" },
  header: { marginBottom: theme.spacing(3) },
  filters: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  filterLabel: { marginRight: theme.spacing(1), fontWeight: 600 },
  entryCard: {
    padding: theme.spacing(2, 3),
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  entryTitle: { fontWeight: 700, fontSize: "1.1rem", marginBottom: theme.spacing(0.5) },
  entryDate: { color: theme.palette.text.secondary, fontSize: "0.85rem", marginBottom: theme.spacing(1.5) },
  section: { marginBottom: theme.spacing(1.5) },
  sectionLabel: {
    display: "inline-block",
    fontWeight: 700,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: 1,
    padding: "2px 8px",
    borderRadius: 4,
    marginBottom: theme.spacing(0.5),
  },
  labelSecurity: { background: "#ffebee", color: "#c62828" },
  labelFixed:    { background: "#e8f5e9", color: "#2e7d32" },
  labelFeature:  { background: "#e3f2fd", color: "#1565c0" },
  labelInfra:    { background: "#f3e5f5", color: "#6a1b9a" },
  bulletList: { margin: "4px 0 0 0", paddingLeft: 20, "& li": { marginBottom: 4, fontSize: "0.9rem" } },
  chip: { cursor: "pointer" },
  activeChip: { fontWeight: 700 },
  loading: { display: "flex", justifyContent: "center", padding: theme.spacing(6) },
  empty: { textAlign: "center", color: theme.palette.text.secondary, padding: theme.spacing(4) },
}));

const CATEGORIES = ["Todos", "Security", "Fixed", "Feature", "Infra"];
const DATE_RANGES = [
  { label: "Últimos 7 días", days: 7 },
  { label: "Últimos 30 días", days: 30 },
  { label: "Todo", days: null },
];

// Parse raw markdown into structured entries
function parseChangelog(raw) {
  const entries = [];
  const lines = raw.split("\n");
  let current = null;
  let currentSection = null;
  let currentBullets = [];

  const flushSection = () => {
    if (current && currentSection && currentBullets.length > 0) {
      current.sections.push({ label: currentSection, bullets: [...currentBullets] });
    }
    currentSection = null;
    currentBullets = [];
  };

  for (const line of lines) {
    const entryMatch = line.match(/^##\s+\[(\d{4}-\d{2}-\d{2})\]\s+[—-]+\s+(.+)$/);
    if (entryMatch) {
      flushSection();
      if (current) entries.push(current);
      current = { date: entryMatch[1], title: entryMatch[2].trim(), sections: [] };
      continue;
    }
    if (!current) continue;

    const sectionMatch = line.match(/^###\s+(.+)$/);
    if (sectionMatch) {
      flushSection();
      currentSection = sectionMatch[1].trim();
      continue;
    }

    if (line.startsWith("- ")) {
      currentBullets.push(line.slice(2).trim());
    }
  }
  flushSection();
  if (current) entries.push(current);
  return entries;
}

const LABEL_CLASS = {
  Security: "labelSecurity",
  Fixed: "labelFixed",
  Feature: "labelFeature",
  Infra: "labelInfra",
};

export default function SuperAdminChangelog() {
  const classes = useStyles();
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("Todos");
  const [dateRange, setDateRange] = useState(DATE_RANGES[2]); // "Todo" default

  useEffect(() => {
    api.get("/superadmin/changelog")
      .then(({ data }) => setContent(data.content || ""))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, []);

  const entries = useMemo(() => parseChangelog(content), [content]);

  const filtered = useMemo(() => {
    const cutoff = dateRange.days
      ? new Date(Date.now() - dateRange.days * 86400000)
      : null;

    return entries.filter((entry) => {
      if (cutoff && new Date(entry.date) < cutoff) return false;
      if (category === "Todos") return true;
      return entry.sections.some((s) => s.label === category);
    }).map((entry) => ({
      ...entry,
      sections: category === "Todos"
        ? entry.sections
        : entry.sections.filter((s) => s.label === category),
    }));
  }, [entries, category, dateRange]);

  if (loading) return <div className={classes.loading}><CircularProgress /></div>;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5" style={{ fontWeight: 700 }}>Changelog</Typography>
        <Typography variant="body2" color="textSecondary">
          Historial de cambios del sistema WAIA
        </Typography>
      </div>

      <div className={classes.filters}>
        <Typography variant="body2" className={classes.filterLabel}>Categoría:</Typography>
        {CATEGORIES.map((cat) => (
          <Chip
            key={cat}
            label={cat}
            size="small"
            color={category === cat ? "primary" : "default"}
            variant={category === cat ? "default" : "outlined"}
            className={`${classes.chip} ${category === cat ? classes.activeChip : ""}`}
            onClick={() => setCategory(cat)}
          />
        ))}
        <span style={{ width: 16 }} />
        <Typography variant="body2" className={classes.filterLabel}>Período:</Typography>
        <ButtonGroup size="small" variant="outlined">
          {DATE_RANGES.map((r) => (
            <Button
              key={r.label}
              color={dateRange.label === r.label ? "primary" : "default"}
              variant={dateRange.label === r.label ? "contained" : "outlined"}
              onClick={() => setDateRange(r)}
            >
              {r.label}
            </Button>
          ))}
        </ButtonGroup>
      </div>

      {filtered.length === 0 ? (
        <Typography className={classes.empty}>No hay entradas para los filtros seleccionados.</Typography>
      ) : (
        filtered.map((entry) => (
          <Paper key={entry.date + entry.title} className={classes.entryCard} elevation={1}>
            <Typography className={classes.entryTitle}>{entry.title}</Typography>
            <Typography className={classes.entryDate}>{entry.date}</Typography>
            {entry.sections.map((sec) => (
              <div key={sec.label} className={classes.section}>
                <span className={`${classes.sectionLabel} ${classes[LABEL_CLASS[sec.label] || "labelFixed"]}`}>
                  {sec.label}
                </span>
                <ul className={classes.bulletList}>
                  {sec.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </Paper>
        ))
      )}
    </div>
  );
}
